import { FieldError } from "@/common/elements/field"
import { Input } from "@/common/elements/input"
import {
  getPaymentCountryLabel,
  getPaymentStateLabel,
  MAX_POSTAL_CODE_LENGTH,
  type BillingAddressFormFields,
  type BillingAddressValidationErrors,
  type PaymentCountryOption,
  type PaymentStateOption,
} from "@/plugins/controls/common/billing-address"

type BillingAddressFieldsProps = {
  value: BillingAddressFormFields
  onChange: (nextValue: BillingAddressFormFields) => void
  validationErrors?: BillingAddressValidationErrors
  countryOptions: PaymentCountryOption[]
  stateOptions: PaymentStateOption[]
  isCountriesLoading?: boolean
  isStatesLoading?: boolean
}

export function BillingAddressFields({
  value,
  onChange,
  validationErrors,
  countryOptions,
  stateOptions,
  isCountriesLoading = false,
  isStatesLoading = false,
}: BillingAddressFieldsProps) {
  function updateValue(nextValue: Partial<BillingAddressFormFields>) {
    onChange({ ...value, ...nextValue })
  }

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">First name</span>
        <Input
          value={value.firstName}
          onChange={(event) => updateValue({ firstName: event.target.value })}
          placeholder="Taylor"
          aria-invalid={validationErrors?.firstName ? true : undefined}
        />
        <FieldError>{validationErrors?.firstName}</FieldError>
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Last name</span>
        <Input
          value={value.lastName}
          onChange={(event) => updateValue({ lastName: event.target.value })}
          placeholder="Jordan"
          aria-invalid={validationErrors?.lastName ? true : undefined}
        />
        <FieldError>{validationErrors?.lastName}</FieldError>
      </label>

      <label className="flex flex-col gap-1 text-sm md:col-span-2">
        <span className="font-medium">Company</span>
        <Input
          value={value.company}
          onChange={(event) => updateValue({ company: event.target.value })}
          placeholder="Optional"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm md:col-span-2">
        <span className="font-medium">Address line 1</span>
        <Input
          value={value.address1}
          onChange={(event) => updateValue({ address1: event.target.value })}
          placeholder="123 Main St"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm md:col-span-2">
        <span className="font-medium">Address line 2</span>
        <Input
          value={value.address2}
          onChange={(event) => updateValue({ address2: event.target.value })}
          placeholder="Suite 210"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">City</span>
        <Input
          value={value.city}
          onChange={(event) => updateValue({ city: event.target.value })}
          placeholder="Seattle"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">State</span>
        <select
          value={value.stateId?.toString() ?? ""}
          onChange={(event) =>
            updateValue({
              stateId: event.target.value.length > 0 ? Number(event.target.value) : null,
            })
          }
          className="h-8 rounded-lg border border-input bg-background px-2.5 disabled:cursor-not-allowed disabled:opacity-60"
          aria-invalid={validationErrors?.stateId ? true : undefined}
          disabled={value.countryId === null || isStatesLoading || stateOptions.length === 0}
        >
          <option value="">
            {value.countryId === null
              ? "Select country first"
              : isStatesLoading
                ? "Loading states..."
                : stateOptions.length === 0
                  ? "No states available"
                  : "Select state or province"}
          </option>
          {stateOptions.map((state) => (
            <option key={state.id} value={state.id}>
              {getPaymentStateLabel(state)}
            </option>
          ))}
        </select>
        <FieldError>{validationErrors?.stateId}</FieldError>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Postal code</span>
        <Input
          value={value.postalCode}
          onChange={(event) => updateValue({ postalCode: event.target.value })}
          placeholder="98104"
          maxLength={MAX_POSTAL_CODE_LENGTH}
          aria-invalid={validationErrors?.postalCode ? true : undefined}
        />
        <FieldError>{validationErrors?.postalCode}</FieldError>
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Country</span>
        <select
          value={value.countryId?.toString() ?? ""}
          onChange={(event) =>
            updateValue({
              countryId: event.target.value.length > 0 ? Number(event.target.value) : null,
              stateId: null,
            })
          }
          className="h-8 rounded-lg border border-input bg-background px-2.5 disabled:cursor-not-allowed disabled:opacity-60"
          aria-invalid={validationErrors?.countryId ? true : undefined}
          disabled={isCountriesLoading}
        >
          <option value="">{isCountriesLoading ? "Loading countries..." : "Select country"}</option>
          {countryOptions.map((country) => (
            <option key={country.id} value={country.id}>
              {getPaymentCountryLabel(country)}
            </option>
          ))}
        </select>
        <FieldError>{validationErrors?.countryId}</FieldError>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Phone</span>
        <Input
          value={value.phone}
          onChange={(event) => updateValue({ phone: event.target.value })}
          placeholder="Optional"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Fax</span>
        <Input
          value={value.fax}
          onChange={(event) => updateValue({ fax: event.target.value })}
          placeholder="Optional"
        />
      </label>
    </div>
  )
}